import type { MessageBubbleEditorProps } from './MessageBubbleEditor.types';
import { MessageBanner } from 'Components/Chat/ChatFooter/MessageInputArea/MessageBanner';
import { ChatLayoutContext } from 'Components/Chat/contexts/ChatLayoutContext';
import { normalizeChatText } from 'Components/Chat/utils';
import type ContentEditable from 'Components/ContentEditable';
import { EmojiButton } from 'Components/shared/EmojiButton';
import { TextButton } from 'Components/shared/TextButton';
import type { MENTION_PREFIX } from 'Constants/enums';
import useCheckLongMessage from 'Hooks/longMessage/useCheckLongMessage';
import { MobXProviderContext } from 'mobx-react';
import { useUIStore } from 'Modules/ui/index.store';
import * as React from 'react';

import type { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import {
  findMentionInsertData,
  focusContentEditable,
  getSelectionRange,
  insertEmoji,
  placeCursorAtEndOfElement,
} from 'Utils/inputNodeUtils';
import { keyIsAlphanumeric, keyIsValidFilterInput } from 'Utils/keyboardInput';
import {
  handleAlphanumericWhenMentionListOpen,
  handleAtSymbol,
  handleBackspaceDeleteWhenMentionListOpen,
  handleEnterKey,
  handleTabRightArrowSpacebarWhenMentionListOpen,
  handleUpDownLeftArrowsWhenMentionListOpen,
} from 'Utils/mentionKeyHandlers';
import {
  preserveSpacesForTurndown,
  convertNbspToSpace,
} from 'Utils/preserveSpacesForTurndown';
import { processMessage } from 'Utils/processMessage';
import { turndownSvc } from 'Utils/turndownSvc';
import { Styled } from './MessageBubbleEditor.styles';
import { ReplyBox } from './ReplyBox';

const testid = 'messageBubbleEditor';

export const MessageBubbleEditor: React.FC<MessageBubbleEditorProps> = ({
  conversationId,
  message,
  compileEditorMarkdown,
}) => {
  const {
    person: { loggedInPersonId },
    ui: {
      setConversationAndTotalUnreadCount,
      selectPersonPresenceStatus,
      selectConversationUnreadCounts,
      setMessageIdToScroll,
    },
    participant: {
      selectParticipantPersons,
      updateMyLastReadMessage,
      selectFilteredOtherParticipantPersonsInCurrentConversation,
    },
    message: {
      editMessage,
      setEditMessageMentionSelectedParticipantId,
      editMessageMentionSelectedParticipantId,
      editMessageMentionFilter,
      setEditMessageMentionFilter,
      editMessageMentionListOpen,
      setEditMessageMentionListOpen,
    },
    conversation: { CurrentConversation },
  } = React.useContext<RootStoreProps>(MobXProviderContext);
  const { setEditMessageId } = React.useContext(ChatLayoutContext);

  const contentEditorRef = React.useRef<ContentEditable>(null);

  const chatText = message.chat?.text_v2 || message.chat?.text;

  const [editMessageDraftRaw, setEditMessageDraftRaw] = React.useState(
    chatText || ''
  );
  const [editMessageDraftHtml, setEditMessageDraftHtml] = React.useState(
    compileEditorMarkdown(
      normalizeChatText(message.chat?.text_v2) || message.chat?.text || ''
    )
      .replace(/\n+$/, '')
      .replace(/\n/g, '<br>')
  );
  const {
    isEmojiPickerOpen: emojiPickerOpen,
    isEditingEmoji: emojiPickerEditing,
    setEmojiPickerState,
  } = useUIStore();

  const [replyBoxRef, setReplyBoxRef] = React.useState(
    message.chat?.text_v2?.match(/^@ref_{[\w\d-]*} /)
  );

  const { messageIsTooLong, checkIsMessageTooLong, resetMessageIsTooLong } =
    useCheckLongMessage();

  const isGroupChat =
    CurrentConversation?.grouping === 'Channel' ||
    CurrentConversation?.grouping === 'Group';

  React.useEffect(() => {
    if (editMessageMentionListOpen) {
      setEditMessageMentionListOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (messageIsTooLong) {
      const v2Message = replyBoxRef
        ? `${String(replyBoxRef)}${editMessageDraftRaw}`
        : editMessageDraftRaw;

      // If message is now within limits, reset the warning
      if (!checkIsMessageTooLong(v2Message)) {
        resetMessageIsTooLong();
      }
    }
  }, [
    editMessageDraftRaw,
    replyBoxRef,
    messageIsTooLong,
    checkIsMessageTooLong,
    resetMessageIsTooLong,
  ]);

  let isDeadkeyPressed = false;
  let previousKeyWasDeadKey = false;

  const insertMentionAtCursor = (prefix: MENTION_PREFIX, value: string) => {
    // Use actual DOM HTML for HTML manipulation (preserves mention divs)
    const actualHtml = contentEditorRef.current?.htmlEl?.innerHTML || '';

    const { htmlStart, htmlEnd } = contentEditorRef.current.selectionRange;

    let draftHtmlWithMentions: string;

    if (prefix === '@pr') {
      const personId = parseInt(value, 10);
      const participants = selectParticipantPersons(conversationId);
      const mentioned = participants?.find(
        (p) => p.person.sourceId === personId.toString()
      ); // Mentioned Participant/Person
      if (mentioned !== undefined) {
        const htmlInsertData = findMentionInsertData(actualHtml, htmlStart);
        draftHtmlWithMentions =
          actualHtml.substring(0, htmlInsertData.insertIndex) +
          `${htmlInsertData.prependWith}&nbsp;<div class="input-mention" mention-target="${prefix}${value}" contenteditable="false">@${mentioned.person.name}</div>&nbsp;` +
          actualHtml.substring(htmlEnd);
      } else {
        return; // Can't find participant, don't insert
      }
    } else {
      const htmlInsertData = findMentionInsertData(actualHtml, htmlStart);
      draftHtmlWithMentions =
        actualHtml.substring(0, htmlInsertData.insertIndex) +
        `${htmlInsertData.prependWith}&nbsp;<div class="input-mention" mention-target="${prefix}${value}" contenteditable="false">${prefix}${value}</div>&nbsp;` +
        actualHtml.substring(htmlEnd);
    }

    // Update HTML first
    setEditMessageDraftHtml(draftHtmlWithMentions);
    setEditMessageMentionListOpen(false);

    // Generate raw text from HTML using turndownSvc to ensure @pr{number} format is preserved
    // This correctly converts all mention divs back to @pr{number} format
    const htmlWithPreservedSpaces = preserveSpacesForTurndown(
      draftHtmlWithMentions.replace(/\n/gi, '<br>')
    );
    let rawFromHtml = turndownSvc.turndown(htmlWithPreservedSpaces);
    rawFromHtml = convertNbspToSpace(rawFromHtml);
    setEditMessageDraftRaw(rawFromHtml);

    // Place cursor after the inserted mention (ContentEditable restores this on update)
    const startPosition =
      contentEditorRef.current.selectionRange.start +
      value.length +
      prefix.length +
      1;
    contentEditorRef.current.selectionRange = {
      ...contentEditorRef.current.selectionRange,
      start: startPosition,
      end: startPosition,
    };
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const htmlValue = e.target.value;
    const htmlForTurndown = htmlValue?.replace(/\n/g, '<br>');
    const htmlWithPreservedSpaces = preserveSpacesForTurndown(htmlForTurndown);
    let rawResultValue = turndownSvc.turndown(htmlWithPreservedSpaces);
    rawResultValue = convertNbspToSpace(rawResultValue);

    if (!isDeadkeyPressed) {
      setEditMessageDraftRaw(rawResultValue);
      setEditMessageDraftHtml(rawResultValue ? htmlValue : '');
    }
  };

  const submitEditMessage = async () => {
    const v2Message = replyBoxRef
      ? `${replyBoxRef} ${editMessageDraftRaw}`
      : editMessageDraftRaw;

    if (checkIsMessageTooLong(v2Message)) {
      return;
    }

    if (editMessageDraftRaw === chatText) {
      setEditMessageMentionListOpen(false);
      return setEditMessageId('');
    }
    if (editMessageDraftHtml.includes('class="input-mention"')) {
      pushToGTMDataLayer('mention', { conversationId });
    }

    await editMessage(conversationId, message.id, {
      text: editMessageDraftRaw,
      text_v2: v2Message,
    });

    setEditMessageMentionListOpen(false);
    setEditMessageId('');

    setEditMessageDraftRaw('');
    setEditMessageDraftHtml('');
  };

  const editOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (previousKeyWasDeadKey && !isDeadkeyPressed) {
      previousKeyWasDeadKey = false;
      return;
    }

    if (e.key === 'Dead') {
      isDeadkeyPressed = true;
      previousKeyWasDeadKey = true;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      if (contentEditorRef.current?.htmlEl === document.activeElement) {
        emojiPickerOpen
          ? setEmojiPickerState({
              isEmojiPickerOpen: false,
              isEditingEmoji: false,
            })
          : setEditMessageId('');
      }
    }

    // Enter
    if (e.key === 'Enter') {
      void handleEnterKey(
        e,
        true, // TODO: Pass prop down from PusherStore (RP 2018-11-21)
        conversationId,
        editMessageMentionListOpen,
        editMessageMentionSelectedParticipantId,
        insertMentionAtCursor,
        selectParticipantPersons,
        submitEditMessage
      );
    }

    // @ symbol (shift + 2), pop up the mentions list
    if (isGroupChat && e.key === '@') {
      // Use actual innerText from ContentEditable (not trimmed draft)
      const actualText = contentEditorRef.current?.htmlEl?.innerText || '';
      handleAtSymbol(
        e,
        contentEditorRef.current.selectionRange,
        actualText,
        editMessageMentionListOpen,
        setEditMessageMentionListOpen,
        setEditMessageMentionSelectedParticipantId,
        setEmojiPickerState
      );
    }

    // Handle Up/Down arrows, Backspace, and Delete when the Mention list is open
    if (isGroupChat && editMessageMentionListOpen) {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft'].includes(e.key)) {
        handleUpDownLeftArrowsWhenMentionListOpen(
          e,
          selectFilteredOtherParticipantPersonsInCurrentConversation,
          editMessageMentionSelectedParticipantId,
          setEditMessageMentionSelectedParticipantId,
          setEditMessageMentionListOpen,
          editMessageMentionFilter
        );
      } else if (['ArrowRight', 'Tab', ' '].includes(e.key)) {
        handleTabRightArrowSpacebarWhenMentionListOpen(
          e,
          editMessageMentionSelectedParticipantId,
          selectParticipantPersons,
          conversationId,
          insertMentionAtCursor
        );
      } else if (['Backspace', 'Delete'].includes(e.key)) {
        handleBackspaceDeleteWhenMentionListOpen(
          e,
          contentEditorRef.current.selectionRange,
          editMessageDraftHtml,
          editMessageMentionListOpen,
          setEditMessageMentionListOpen,
          editMessageMentionFilter,
          setEditMessageMentionFilter,
          selectFilteredOtherParticipantPersonsInCurrentConversation,
          setEditMessageMentionSelectedParticipantId
        );
      } else if (keyIsAlphanumeric(e)) {
        handleAlphanumericWhenMentionListOpen(
          e,
          editMessageMentionListOpen,
          editMessageMentionFilter,
          setEditMessageMentionFilter,
          selectFilteredOtherParticipantPersonsInCurrentConversation,
          setEditMessageMentionSelectedParticipantId
        );
      }
      // Any invalid inputs should close the Mention list to avoid auto-complete desync
      else if (!keyIsValidFilterInput(e)) {
        setEditMessageMentionListOpen(false);
      }
    }
  };

  const scrollViewportToCurrentMessage = () => {
    setMessageIdToScroll(message.id);
  };

  const toggleShowEmojis = (open: boolean) => {
    if (open) {
      scrollViewportToCurrentMessage();
      setEmojiPickerState({ isEmojiPickerOpen: true, isEditingEmoji: true });
      setEditMessageMentionListOpen(false);
      if (contentEditorRef.current?.htmlEl) {
        const contentEditorRefSelection = getSelectionRange(
          contentEditorRef.current.htmlEl
        );
        // Focus and move to end if there is no selection (unfocused) in the input
        if (
          contentEditorRefSelection.start === 0 &&
          contentEditorRefSelection.end === 0
        ) {
          focusContentEditable(contentEditorRef.current);
          placeCursorAtEndOfElement(contentEditorRef.current.htmlEl);
        }
      }
    } else {
      setEmojiPickerState({ isEmojiPickerOpen: false, isEditingEmoji: false });
    }
  };

  return (
    <Styled.EditWrapper $hasBanner={messageIsTooLong} className="edit-wrapper">
      <MessageBanner
        text="It seems that your message is too long. Please shorten it."
        canClose={false}
        isVisible={messageIsTooLong}
      />
      <Styled.InputArea>
        {replyBoxRef && message.references?.[0] && (
          <ReplyBox
            {...{ reference: message.references[0] }}
            onRemoveClick={() => {
              setEditMessageDraftRaw(normalizeChatText(editMessageDraftRaw));
              setReplyBoxRef(null);
            }}
          />
        )}
        <Styled.ContentEditable
          ref={contentEditorRef}
          className="edit-box"
          placeholder="Type your message"
          html={editMessageDraftHtml}
          onKeyDown={editOnKeyDown}
          onKeyUp={(e) => e.key === 'Dead' && (isDeadkeyPressed = false)}
          updateMessage={(inputValue) =>
            processMessage({
              inputValue,
              onHandleDraftRaw: setEditMessageDraftRaw,
              onHandleDraftHtml: setEditMessageDraftHtml,
              isDeadKeyPressed: isDeadkeyPressed,
            })
          }
          {...{ onChange }}
        />
        <Styled.HoverButtons>
          {isGroupChat && (
            <Styled.AtMentionMembers
              {...{
                conversationId,
                getEmojiPickerState: () => ({
                  isEmojiPickerOpen: emojiPickerOpen,
                  isEditingEmoji: emojiPickerEditing,
                }),
                insertMentionAtCursor,
                contentEditableRef: contentEditorRef,
                loggedInPersonId,
                mentionListOpen: editMessageMentionListOpen,
                messageMentionFilter: editMessageMentionFilter,
                selectedMentionParticipantId:
                  editMessageMentionSelectedParticipantId,
                selectFilteredOtherParticipantPersonsInCurrentConversation,
                selectParticipantPersons,
                selectPersonPresenceStatus,
                selectUnreadCounts: selectConversationUnreadCounts,
                setMentionListOpen: setEditMessageMentionListOpen,
                setMessageMentionFilter: setEditMessageMentionFilter,
                setSelectedMentionParticipantId:
                  setEditMessageMentionSelectedParticipantId,
                setEmojiPickerState,
                setConversationAndTotalUnreadCount,
                updateMyLastReadMessage,
              }}
              onKeyDown={editOnKeyDown}
              onOpen={scrollViewportToCurrentMessage}
              resolveConversationLinkPath={null}
              openUpwards={true}
            />
          )}
          <EmojiButton
            isOpen={emojiPickerOpen && emojiPickerEditing}
            onOpen={() => toggleShowEmojis(true)}
            onClose={() => toggleShowEmojis(false)}
            onChooseEmoji={(emoji) =>
              insertEmoji(emoji, contentEditorRef.current)
            }
            relativePosition
          />
        </Styled.HoverButtons>
      </Styled.InputArea>
      <Styled.EditActions>
        <Styled.CancelButton
          onClick={() => {
            setEditMessageMentionListOpen(false);
            setEditMessageId('');
          }}
          testid={`${testid}-buttonClose`}
          variant="secondary"
        >
          Cancel
        </Styled.CancelButton>
        <TextButton
          onClick={submitEditMessage}
          disabled={{
            condition: !editMessageDraftRaw || messageIsTooLong,
            title: !editMessageDraftRaw
              ? 'Type a message before saving'
              : 'Shorten your message first',
          }}
          variant="primary"
          testid={`${testid}-buttonSave`}
        >
          Save
        </TextButton>
      </Styled.EditActions>
    </Styled.EditWrapper>
  );
};
